import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Modal,
  Typography,
  Paper,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControl,
  TextField
} from "@mui/material";
import DoneOutlineOutlinedIcon from "@mui/icons-material/DoneOutlineOutlined";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GeneralContext from "../GeneralContext/GeneralContext";
import CloseIcon from "@mui/icons-material/Close";
import PageButtonWhite from "./PageButtonWhite";
import PageButtonBlack from "./PageButtonBlack";
import PageButtonBlue from "./PageButtonBlue";
import Grid from "@mui/material/Grid2";
import axios from "../../api/axios";

import * as XLSX from "xlsx";
import {NO_EES, NO_EES_TEMP, NO_SUBTEL, NO_MINEDUC, ROL_REP_EES, ROL_REP_EES_TEMP, ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL, ROL_MINEDUC_ADMIN, DAYS, DEFAULT_ITEMS_CONTINGENCIAS, MAX_JUSTIFICACION} from "../../Constants/Constants";


export default function ModalStandard(props) {
  const {
    open,
    onClose,   // Ahora espera dos parámetros: (started: boolean, successfulCount: number)
    btnText,
    selected = [],
    action,
  } = props;
  const { userSesion } =  useContext(GeneralContext);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [processed, setProcessed] = useState(0);
  const [started, setStarted] = useState(false);
  const [resolution, setResolution] = useState("El rechazo se determinó según los antecedentes disponibles.")

  // Contámos éxitos y fallidos en base a `results`
  const successfulCount = results.filter((r) => r.status === "Exitoso").length;
  const failedCount = results.filter((r) => r.status === "Fallido").length;

  useEffect(() => {
    if (open) {
      setResults([]);
      setProcessed(0);
      setLoading(false);
      setStarted(false);
    }
  }, [open, selected]);

  const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const callActions = useCallback(async () => {
    if (!selected || selected.length === 0) {
      return;
    }
    setStarted(true);
    setLoading(true);

    const chunks = chunkArray(selected, 5);
    let allResults = [];

    for (const chunk of chunks) {
      try {
        let response;
        if (action === "Rechazar") {
          response = await axios.massiveRejection("contingencias", { ids: chunk, motivo: resolution });
        } else if (action === "Aprobar") {
          response = await axios.massiveApproval("contingencias", { ids: chunk });
        } else {
          throw new Error("Acción no válida");
        }

        const data = response.data;
        if (data && Array.isArray(data.results)) {
          allResults = allResults.concat(data.results);
        } else {
          const fallback = chunk.map((id) => ({
            id,
            status: "Fallido",
            detail: "Respuesta inválida",
          }));
          allResults = allResults.concat(fallback);
        }
      } catch (err) {
        const errorResults = chunk.map((id) => ({
          id,
          status: "Fallido",
          detail: err.message || "Error en petición",
        }));
        allResults = allResults.concat(errorResults);
      }

      setResults([...allResults]);
      setProcessed(allResults.length);
    }

    setLoading(false);
  }, [action, selected, resolution]);

  if (!open) return null;

  // Función de descarga XLS que invoca onClose con started y successfulCount
  const downloadXLS = () => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(results);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resultados");
    XLSX.writeFile(workbook, "resultados.xlsx");
    onClose(started, successfulCount);
  };

  return (
    <Modal
      open={open}
      // Al hacer click fuera del contenido o presionar ESC, cierra pasando ambos parámetros
      onClose={() => onClose(true, successfulCount)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      }}
      slotProps={{ backdrop: { sx: { opacity: "0.1!important" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "800px",
          maxHeight: "600px",
          backgroundSize: "background.paper",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: 0,
          overflowY: "auto",
        }}
      >
        <Paper
          elevation={3}
          sx={{ padding: 3, textAlign: "center", position: "relative" }}
        >
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8 }}
            onClick={() => onClose(true, successfulCount)}
          >
            <CloseIcon />
          </IconButton>

          {!started && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ mb: 0 }}>
                  <WarningAmberIcon color="error" sx={{ fontSize: 45 }} />
              </Box>

          <Typography
            variant="h2"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{
              mb: 1,
              color: "#4D4D4D"
            }}
          >
            Acción en masa ({action.toUpperCase()})
          </Typography>

          <Typography variant="h6" color="textSecondary" gutterBottom sx={{fontWeight: '700', color: "#333"}}>
                ¿Esta seguro de {action.toUpperCase()} ({selected.length} documentos)?
              </Typography>

              <Typography variant="body1" color="textSecondary" sx={{px: 5, color: "#333"}}>
                {action === "Rechazar" ? "Esta acción no podrá deshacerse, de lo contrario el operador deberá generar un nuevo documento." : "Esta acción no podrá deshacerse, y afectará a los siguientes procesos."}
              </Typography>


              {action === "Rechazar" ? (
                <>

              <Divider sx={{ my: 2 }} />

                    <Typography variant="h3" align="left" color="inherit" noWrap sx={{ml: 1}}>
                      JUSTIFICACIÓN DE RESOLUCIÓN
                    </Typography>

                    <FormControl sx={{ m: 1, width: "100%", spacing: 2 }}>
                      <TextField test_id="resolution" inputProps={{ maxLength: MAX_JUSTIFICACION }}
                        multiline
                        minRows={4}
                        maxRows={30}
                        id="outlined-required"
                        placeholder="Texto aquí"
                        size="small"
                        value={resolution}
                        onChange={(event) =>
                          setResolution( event.target.value)
                        }
                      />
                    </FormControl>
                    </>
                ) : null}

            </Box>
          )}
          

          {started && (
            <>
              
              <Typography
            variant="h5"
            component="h2"
            fontWeight="bold"
            gutterBottom
            sx={{
              fontSize: "26px",
              my: 1,
              color: "#4D4D4D",
              textTransform: "uppercase"
            }}
          >
            Acción en masa ({action.toUpperCase()})
          </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 4,
                  my: 2,
                  alignItems: "center",
                }}
              >

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <DoneOutlineOutlinedIcon color="success" />
                  <Typography variant="body1" fontWeight="bold">
                    {successfulCount} Exitosos
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ErrorOutlineIcon color="error" />
                  <Typography variant="body1" fontWeight="bold">{failedCount} Fallidos</Typography>
                </Box>
              </Box>

              <Box sx={{ my: 1 }}>
                <Typography variant="body1" align="center">
                  Procesados: {processed} / {selected.length}
                </Typography>
                {loading && <LinearProgress sx={{ mt: 1 }} />}
              </Box>

              <TableContainer
                sx={{
                  padding: 1,
                  maxHeight: "300px",
                  overflow: "auto",
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": { width: "8px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555",
                  },
                  mb: 2,
                }}
              >
                <Table  stickyHeader sx={{ borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{borderRight: "1px solid rgba(0, 0, 0, 0.30)"}}>ID</TableCell>
                      <TableCell align="center" sx={{borderRight: "1px solid rgba(0, 0, 0, 0.30)"}}>Estado</TableCell>
                      <TableCell align="center">Detalle</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((row) => (
                      <TableRow sx={{ "&:last-child td": {  borderBottom: "none", }, }} >
                        <TableCell align="center" sx={{borderRight: "1px solid rgba(0, 0, 0, 0.30)"}}>{row.id}</TableCell>
                        <TableCell align="center" sx={{borderRight: "1px solid rgba(0, 0, 0, 0.30)"}}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center"}}>
                          {row.status === "Exitoso" ? (     
                  <><DoneOutlineOutlinedIcon color="success"/><Typography variant="body1">{row.status}</Typography></>) :
                (<><ErrorOutlineIcon color="error"/><Typography variant="body1">{row.status}</Typography></>)}
                </Box>

                        </TableCell>
                        <TableCell align="center">{row.detail || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mb: 2, pt: 1 }}>
                {loading ? (
                  <Typography variant="body2" color="textSecondary">
                    No cierre esta ventana mientras se realiza el proceso
                  </Typography>
                ) : (
                  <>
                    <PageButtonBlue
                    width="350px"
                      onClick={downloadXLS}
                      disabled={loading}
                    >
                      Cerrar y descargar .xlsx de resumen
                    </PageButtonBlue>
                  </>
                )}
              </Box>
            </>
          )}

          {!started && (
          <Grid container spacing={2} sx={{pt: 1}}>
          <Grid size={3}/>
          <Grid size={3}>
            <PageButtonWhite onClick={() => onClose(true, successfulCount)} width="100%">
              Cancelar
            </PageButtonWhite>

          </Grid>

          <Grid size={5}>
            <PageButtonBlack onClick={callActions} disabled={loading} width="100%">
              OK, {action} masivamente
            </PageButtonBlack>
          </Grid>

          </Grid>
          )}
        </Paper>
      </Box>
    </Modal>
  );
}
